import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { Box, BoxProps, HStack, IconButton, Portal, rem, Text, VStack, XOutline } from '@bonusly/design-system';
import { useSpoofing } from '@bonusly/who-am-i';

const SPOOFING_BANNER_HEIGHT = 60;
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 56;

interface PanelProps extends BoxProps {
	closeButtonLabel: string;
	isOpen: boolean;
	isSidebarExpanded: boolean;
	onClose: () => void;
	title: string;
}

export const Panel: FC<PropsWithChildren<PanelProps>> = ({ children, closeButtonLabel, isOpen, isSidebarExpanded, onClose, title, ...props }) => {
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const { isSpoofing, isDemoing } = useSpoofing();
	const isSpoofingBannerVisible = isSpoofing && !isDemoing;
	const sidebarWidth = isSidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

	// Focus the close button when panel opens
	useEffect(() => {
		if (isOpen) {
			requestAnimationFrame(() => {
				closeButtonRef.current?.focus();
			});
		}
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	return (
		<Portal>
			<Box
				position="fixed"
				top={isSpoofingBannerVisible ? `${SPOOFING_BANNER_HEIGHT + 4}px` : rem(4)}
				left={`${sidebarWidth + 4}px`}
				bottom={rem(8)}
				bg="bg.surface"
				border="1px solid"
				borderColor="border.default"
				borderRadius="md"
				zIndex="dropdown"
				overflowY="auto"
				boxShadow="lg"
				py={rem(20)}
				px={rem(8)}
				w={rem(350)}
				{...props}
			>
				<VStack spacing={0} align="stretch">
					{/* Header with title and close button */}
					<HStack justify="space-between" align="center" px={rem(12)} pb={rem(12)}>
						<Text fontSize={200} fontWeight="semibold">
							{title}
						</Text>
						<IconButton
							ref={closeButtonRef}
							aria-label={closeButtonLabel}
							icon={<XOutline />}
							size="sm"
							bg="secondary.bgAlpha"
							borderRadius="full"
							onClick={onClose}
							_focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', borderRadius: 'full', outlineOffset: '-2px' }}
							data-testid="panel-close"
						/>
					</HStack>

					{children}
				</VStack>
			</Box>
		</Portal>
	);
};
